import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { getWorkPlan, updateWorkPlan } from '../../services/workPlanService';
import api from '../../services/api';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Button from '../../components/ui/Button';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import PolicyRulesPanel from '../../components/ui/PolicyRulesPanel';
import { useAuth } from '../../context/AuthContext';

/* ── Styled Components ── */

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
`;

const StickyHeader = styled.div`
  flex-shrink: 0;
`;

// Tek kaydırma: içerik kabı küçülebilir (min-height:0) ve taşmaz; kayan yalnız atama kuralları
// tablosunun satırlarıdır (PolicyRulesPanel kalan yüksekliği ölçer).
const TabContent = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding-top: 8px;
`;

const ScheduleSection = styled.div`
  flex-shrink: 0;
`;

const InfoCard = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 24px 28px;
  margin-bottom: 20px;
  box-shadow: 0 1px 4px rgba(0, 66, 66, 0.06);
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const PlanTitle = styled.h2`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`;

const DefaultBadge = styled.span`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  background: #16a34a15;
  color: #16a34a;
`;

/* ── Weekly Schedule ── */

const SectionTitle = styled.h3`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 16px 0;
`;

const ScheduleHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const WeekGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 12px;
  margin-bottom: 20px;
`;

const DayCard = styled.div`
  background: ${({ $color }) => $color ? `${$color}15` : '#f1f5f9'};
  border-radius: 6px;
  padding: 16px;
  text-align: center;
`;

const DayLabel = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 8px;
`;

const DayValue = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const DayShortCode = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  margin-top: 4px;
`;

const OffLabel = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #94a3b8;
`;

const RestBadge = styled.div`
  display: inline-block;
  margin-top: 6px;
  padding: 2px 8px;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 11px;
  font-weight: 600;
  background: rgba(2, 145, 239, 0.1);
  color: #0291EF;
`;

/* ── Day keys mapping ── */

// `dow` = .NET DayOfWeek (0 = Pazar … 6 = Cumartesi) — WorkPlan.WeeklyRestDay bu sayıyı taşır.
export const DAY_KEYS = [
  { key: 'monday', idField: 'mon', nameField: 'monName', shortCodeField: 'monShortCode', dow: 1 },
  { key: 'tuesday', idField: 'tue', nameField: 'tueName', shortCodeField: 'tueShortCode', dow: 2 },
  { key: 'wednesday', idField: 'wed', nameField: 'wedName', shortCodeField: 'wedShortCode', dow: 3 },
  { key: 'thursday', idField: 'thu', nameField: 'thuName', shortCodeField: 'thuShortCode', dow: 4 },
  { key: 'friday', idField: 'fri', nameField: 'friName', shortCodeField: 'friShortCode', dow: 5 },
  { key: 'saturday', idField: 'sat', nameField: 'satName', shortCodeField: 'satShortCode', dow: 6 },
  { key: 'sunday', idField: 'sun', nameField: 'sunName', shortCodeField: 'sunShortCode', dow: 0 },
];

// GenericSelect boş değeri "seçilmemiş" sayar; "İşaretsiz" seçeneği görünür bir değer taşısın diye gözcü.
export const NO_WEEKLY_REST_DAY = 'none';

// Hafta tatili günü seçenekleri: "İşaretsiz" + yalnız vardiyası BOŞ günler. O an seçili gün (vardiya sonradan
// verilmiş olsa bile) listede kalır ki kullanıcı seçimini görsün; sunucu workPlan.weeklyRestDayHasShift ile reddeder.
export const weeklyRestDayOptions = (formData, dayLabel, noneLabel) => [
  { id: NO_WEEKLY_REST_DAY, name: noneLabel },
  ...DAY_KEYS
    .filter(({ idField, dow }) => !formData?.[idField] || String(formData?.weeklyRestDay) === String(dow))
    .map(({ key, dow }) => ({ id: dow, name: dayLabel(key) })),
];

// PUT gövdesi: adı ve varsayılan bayrağını korur, günleri ve hafta tatili gününü yazar.
export const buildSchedulePayload = (plan, formData) => {
  const payload = { name: plan.name, isDefault: plan.isDefault };
  DAY_KEYS.forEach(({ idField }) => { payload[idField] = formData?.[idField] || null; });
  const rest = formData?.weeklyRestDay;
  payload.weeklyRestDay = rest === NO_WEEKLY_REST_DAY || rest === null || rest === undefined || rest === ''
    ? null
    : Number(rest);
  return payload;
};

export default function WorkPlanDetailsPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const { canWrite } = useAuth();
  const [plan, setPlan] = useState(null);
  const [scheduleDrawerOpen, setScheduleDrawerOpen] = useState(false);

  // Çalışma planı uçları ve kural türü `workPlan` EM modülündedir (backend WorkPlanController,
  // PolicyRuleKinds.ModuleFor) — yazma kapısı da EM.
  const hasWrite = canWrite('EM');

  /* ── Data fetching ── */

  const fetchPlan = useCallback(() => {
    // api.js GET'te reddetmez: sinyal res.data.status.
    getWorkPlan(id).then((res) => {
      if (res?.data?.status === true) setPlan(res.data.data);
    });
  }, [id]);

  useEffect(() => {
    fetchPlan();
  }, [fetchPlan]);

  /* ── Day labels ── */

  const dayLabel = useCallback((key) => t(`workHour.${key}`), [t]);

  /* ── Schedule edit ── */

  const searchWorkHours = useCallback(async (q) => {
    const params = q ? { 'name[like]': q, pageSize: 20 } : { pageSize: 20 };
    const res = await api.get('/v1/work-hours', { params });
    return (res.data?.data?.data || []).map((wh) => ({
      id: wh.id, name: wh.name, shortCode: wh.shortCode, colorCode: wh.colorCode,
    }));
  }, []);

  // Mevcut seçimin adı planın kendi gün adlarından gelir; bilinmeyen kimlik tek kayıt ucundan okunur.
  const getWorkHourById = useCallback(async (whId) => {
    const known = plan && DAY_KEYS.find(({ idField }) => plan[idField] === whId);
    if (known && plan[known.nameField]) return { id: whId, name: plan[known.nameField] };
    const res = await api.get(`/v1/work-hours/${whId}`);
    return { id: whId, name: res.data?.data?.name || '' };
  }, [plan]);

  const scheduleSchema = useMemo(() => [
    ...DAY_KEYS.map(({ key, idField }) => ({
      name: idField,
      label: dayLabel(key),
      type: 'asyncSelect',
      grid: 12,
      onSearch: searchWorkHours,
      onGetById: getWorkHourById,
    })),
    {
      name: 'weeklyRestDay',
      label: t('workPlan.weeklyRestDay'),
      type: 'select',
      grid: 12,
      idKey: 'id',
      labelKey: 'name',
      options: (formData) => weeklyRestDayOptions(formData, dayLabel, t('workPlan.weeklyRestDayNone')),
    },
  ], [t, dayLabel, searchWorkHours, getWorkHourById]);

  // GenericFormDrawer açıkken initialValues kimliği değişirse formu sıfırlar — plan değişmedikçe sabit.
  const scheduleInitialValues = useMemo(() => {
    if (!plan) return null;
    const values = {};
    DAY_KEYS.forEach(({ idField }) => { values[idField] = plan[idField] || null; });
    values.weeklyRestDay = plan.weeklyRestDay ?? NO_WEEKLY_REST_DAY;
    return values;
  }, [plan]);

  const handleSaveSchedule = useCallback(async (formData) => {
    if (!plan) return false;
    try {
      await updateWorkPlan(id, buildSchedulePayload(plan, formData));
    } catch {
      // interceptor toast'u (ör. workPlan.weeklyRestDayHasShift); çekmece açık kalır.
      return false;
    }
    fetchPlan();
    return true;
  }, [id, plan, fetchPlan]);

  return (
    <PageContainer>
      <StickyHeader>
        <Breadcrumbs items={[
          { label: t('workPlan.title'), path: '/work-plans' },
          { label: plan?.name || '...' },
        ]} />

        {/* ── Info Card ── */}
        {plan && (
          <InfoCard>
            <TitleRow>
              <PlanTitle>{plan.name}</PlanTitle>
              {plan.isDefault && <DefaultBadge>{t('workPlan.default')}</DefaultBadge>}
            </TitleRow>
          </InfoCard>
        )}
      </StickyHeader>

      <TabContent>
        {/* ── Weekly Schedule ── */}
        {plan && (
          <ScheduleSection>
            <ScheduleHeader>
              <SectionTitle>{t('workPlan.weeklySchedule')}</SectionTitle>
              {hasWrite && (
                <Button variant="outline" onClick={() => setScheduleDrawerOpen(true)}>
                  {t('workPlan.editSchedule')}
                </Button>
              )}
            </ScheduleHeader>

            <WeekGrid>
              {DAY_KEYS.map(({ key, idField, nameField, shortCodeField, dow }) => {
                const hasWh = !!plan[idField];
                return (
                  <DayCard key={key} $color={hasWh ? '#0291EF' : null}>
                    <DayLabel>{dayLabel(key)}</DayLabel>
                    {hasWh ? (
                      <>
                        <DayValue>{plan[nameField] || '-'}</DayValue>
                        {plan[shortCodeField] && <DayShortCode>{plan[shortCodeField]}</DayShortCode>}
                      </>
                    ) : (
                      <OffLabel>{t('workPlan.off')}</OffLabel>
                    )}
                    {plan.weeklyRestDay === dow && <RestBadge>{t('workPlan.weeklyRestDay')}</RestBadge>}
                  </DayCard>
                );
              })}
            </WeekGrid>
          </ScheduleSection>
        )}

        {/* ── Atama Kuralları (kişi başı "Ata" çekmecesinin ve atanmış kişi listesinin yerine) ── */}
        <PolicyRulesPanel
          kind="workPlan"
          policyId={id}
          title={t('policyRule.title')}
          canWrite={hasWrite}
        />
      </TabContent>

      {/* ── Schedule Edit Drawer ── */}
      <GenericFormDrawer
        open={scheduleDrawerOpen}
        onClose={() => setScheduleDrawerOpen(false)}
        title={t('workPlan.editSchedule')}
        schema={scheduleSchema}
        initialValues={scheduleInitialValues}
        onSave={handleSaveSchedule}
      />
    </PageContainer>
  );
}
